import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Target, TrendingUp, ChevronDown, ChevronUp, Download, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
  'Meeting Expectation': '#22c55e',
  'Average': '#f97316',
  'Below Expectation': '#ef4444'
};

const getPerformanceCategory = (score) => {
  if (score >= 80) return 'Meeting Expectation';
  if (score >= 50) return 'Average';
  return 'Below Expectation';
};

export default function DepartmentDashboard() {
  const navigate = useNavigate();
  const { deptId } = useParams();
  const [department, setDepartment] = useState(null);
  const [kras, setKras] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSOP, setOpenSOP] = useState({});
  const [reportStartMonth, setReportStartMonth] = useState('');
  const [reportEndMonth, setReportEndMonth] = useState('');

  const toggleSOP = (kpiId) => {
    setOpenSOP(prev => ({ ...prev, [kpiId]: !prev[kpiId] }));
  };

  const downloadKRAsKPIsPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text(`${department?.name} Department - KRAs & KPIs`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);
    
    let yPosition = 35;
    
    kras.forEach((kra, kraIndex) => {
      const kraKpis = kpis.filter(kpi => kpi.kra_id === kra.id);
      const totalWeightage = kraKpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
      
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // KRA Header
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`KRA ${kraIndex + 1}: ${kra.title}`, 14, yPosition);
      yPosition += 7;
      
      if (kra.description) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const splitDesc = doc.splitTextToSize(kra.description, 180);
        doc.text(splitDesc, 14, yPosition);
        yPosition += splitDesc.length * 5 + 5;
      }
      
      // KPIs Table
      if (kraKpis.length > 0) {
        const tableData = kraKpis.map((kpi, idx) => [
          idx + 1,
          kpi.title,
          `${kpi.weightage}%`,
          kpi.description || '-'
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          head: [['#', 'KPI', 'Weightage', 'Description']],
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [99, 102, 241] },
          margin: { left: 14, right: 14 },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 70 },
            2: { cellWidth: 25 },
            3: { cellWidth: 75 }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      }
      
      // Total weightage
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Total Weightage for this KRA: ${totalWeightage}%`, 14, yPosition);
      yPosition += 10;
    });
    
    // Overall total
    const overallTotal = kpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Overall Total Weightage: ${overallTotal}%`, 14, yPosition);
    
    doc.save(`${department?.name}_KRAs_KPIs.pdf`);
    toast.success('PDF downloaded successfully');
  };

  const downloadSOPsPDF = () => {
    const kpisWithSOPs = kpis.filter(kpi => kpi.sop);
    
    if (kpisWithSOPs.length === 0) {
      toast.error('No SOPs available to download');
      return;
    }
    
    const doc = new jsPDF();
    
    // Helper function to remove emojis and special characters that jsPDF can't render
    const sanitizeText = (text) => {
      if (!text) return '';
      // Remove all emoji characters and other problematic Unicode characters
      return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim();
    };
    
    // Title
    doc.setFontSize(18);
    doc.text(`${department?.name} Department - SOPs`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);
    
    let yPosition = 35;
    
    kras.forEach((kra, kraIndex) => {
      const kraKpisWithSOPs = kpis.filter(kpi => kpi.kra_id === kra.id && kpi.sop);
      
      if (kraKpisWithSOPs.length === 0) return;
      
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // KRA Header
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      const sanitizedKraTitle = sanitizeText(kra.title);
      doc.text(`KRA ${kraIndex + 1}: ${sanitizedKraTitle}`, 14, yPosition);
      yPosition += 7;
      
      if (kra.description) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'italic');
        const sanitizedKraDesc = sanitizeText(kra.description);
        const splitDesc = doc.splitTextToSize(sanitizedKraDesc, 180);
        doc.text(splitDesc, 14, yPosition);
        yPosition += splitDesc.length * 5 + 5;
      }
      
      // Create table data for SOPs
      kraKpisWithSOPs.forEach((kpi) => {
        const sanitizedKpiTitle = sanitizeText(kpi.title);
        const sanitizedKpiDesc = sanitizeText(kpi.description || '-');
        const sanitizedSOP = sanitizeText(kpi.sop);
        
        const tableData = [[
          sanitizedKpiTitle,
          sanitizedKpiDesc,
          sanitizedSOP
        ]];
        
        autoTable(doc, {
          startY: yPosition,
          head: [['KPI', 'Description', 'Standard Operating Procedure']],
          body: tableData,
          theme: 'grid',
          headStyles: { 
            fillColor: [99, 102, 241],
            fontSize: 10,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 9
          },
          margin: { left: 14, right: 14 },
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 50 },
            2: { cellWidth: 80 }
          },
          didDrawPage: (data) => {
            yPosition = data.cursor.y + 5;
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 5;
      });
      
      yPosition += 5;
    });
    
    doc.save(`${department?.name}_SOPs.pdf`);
    toast.success('SOPs PDF downloaded successfully');
  };

  const downloadReviewsPDF = () => {
    if (reviews.length === 0) {
      toast.error('No reviews to download');
      return;
    }
    
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text(`${department?.name} Department - Review History`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);
    
    let yPosition = 35;
    
    reviews.forEach((review, reviewIndex) => {
      const totalWeightage = kpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
      
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Review Header
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`Review ${reviewIndex + 1}: ${review.month} ${review.year}`, 14, yPosition);
      yPosition += 7;
      
      doc.setFontSize(11);
      doc.text(`Overall Score: ${review.overall_score.toFixed(1)}% / ${totalWeightage}%`, 14, yPosition);
      yPosition += 7;
      
      if (review.overall_feedback) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text("Promoter's Feedback:", 14, yPosition);
        yPosition += 5;
        const splitFeedback = doc.splitTextToSize(review.overall_feedback, 180);
        doc.text(splitFeedback, 14, yPosition);
        yPosition += splitFeedback.length * 5 + 5;
      }
      
      // KPIs by KRA
      kras.forEach(kra => {
        const kraKpis = kpis.filter(kpi => kpi.kra_id === kra.id);
        if (kraKpis.length === 0) return;
        
        if (yPosition > 240) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kra.title}`, 14, yPosition);
        yPosition += 7;
        
        // Add KRA description if available
        if (kra.description) {
          doc.setFontSize(9);
          doc.setFont(undefined, 'italic');
          const splitDesc = doc.splitTextToSize(kra.description, 180);
          doc.text(splitDesc, 14, yPosition);
          yPosition += splitDesc.length * 4 + 3;
        }
        
        const tableData = kraKpis.map(kpi => {
          const kpiRev = review.kpi_reviews.find(kr => kr.kpi_id === kpi.id);
          return [
            kpi.title,
            `${kpi.weightage}%`,
            `${kpiRev?.achieved_percentage || 0}%`,
            kpiRev?.feedback || '-'
          ];
        });
        
        autoTable(doc, {
          startY: yPosition,
          head: [['KPI', 'Weightage', 'Achieved', 'Feedback']],
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [99, 102, 241] },
          margin: { left: 14, right: 14 },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 25 },
            2: { cellWidth: 25 },
            3: { cellWidth: 70 }
          }
        });
        
        yPosition = doc.lastAutoTable.finalY + 8;
      });
      
      yPosition += 5;
    });
    
    doc.save(`${department?.name}_Reviews.pdf`);
    toast.success('PDF downloaded successfully');
  };

  const downloadComprehensiveReport = async (startMonth, endMonth) => {
    try {
      toast.info('Generating comprehensive report...');
      
      // Build query params
      const params = new URLSearchParams();
      if (startMonth) params.append('start_month', startMonth);
      if (endMonth) params.append('end_month', endMonth);
      
      const response = await axios.get(`${API}/reports/department/${deptId}?${params.toString()}`);
      const reportData = response.data;
      
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(18);
      doc.text(`${department?.name} Department - Comprehensive Report`, 14, 20);
      doc.setFontSize(10);
      let titleYPos = 28;
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, titleYPos);
      
      if (startMonth || endMonth) {
        titleYPos += 6;
        doc.text(`Period: ${startMonth || 'All'} to ${endMonth || 'All'}`, 14, titleYPos);
      }
      
      let yPosition = titleYPos + 10;
      
      // Process each KRA
      reportData.kras.forEach((kra, kraIndex) => {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        // KRA Header
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA ${kraIndex + 1}: ${kra.title}`, 14, yPosition);
        yPosition += 8;
        
        // KRA Description
        if (kra.description) {
          doc.setFontSize(10);
          doc.setFont(undefined, 'italic');
          const splitDesc = doc.splitTextToSize(kra.description, 180);
          doc.text(splitDesc, 14, yPosition);
          yPosition += splitDesc.length * 5 + 5;
        }
        
        // Process each KPI - show KPI info and reviews in a table
        kra.kpis.forEach((kpi) => {
          if (yPosition > 240) {
            doc.addPage();
            yPosition = 20;
          }
          
          // KPI Title - ensure proper text wrapping
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          const kpiTitleLines = doc.splitTextToSize(`KPI: ${kpi.title}`, 175);
          doc.text(kpiTitleLines, 14, yPosition);
          yPosition += kpiTitleLines.length * 5 + 2;
          
          // KPI Description - ensure proper text wrapping
          doc.setFontSize(9);
          doc.setFont(undefined, 'normal');
          if (kpi.description) {
            const splitKpiDesc = doc.splitTextToSize(`Description: ${kpi.description}`, 175);
            doc.text(splitKpiDesc, 14, yPosition);
            yPosition += splitKpiDesc.length * 4.5 + 3;
          }
          doc.text(`Weightage: ${kpi.weightage}%`, 14, yPosition);
          yPosition += 5;
          
          // Reviews table for this KPI
          if (kpi.review_data && kpi.review_data.length > 0) {
            const reviewTableData = kpi.review_data.map(review => [
              `${review.month} ${review.year}`,
              `${kpi.weightage}%`,
              `${review.achieved_percentage}%`,
              review.feedback || '-'
            ]);
            
            autoTable(doc, {
              startY: yPosition,
              head: [['Month/Year', 'Target', 'Achieved', 'Feedback']],
              body: reviewTableData,
              theme: 'grid',
              headStyles: { fillColor: [99, 102, 241], fontSize: 9 },
              bodyStyles: { fontSize: 8 },
              margin: { left: 14, right: 14 },
              columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 25 },
                2: { cellWidth: 25 },
                3: { cellWidth: 100 }
              }
            });
            
            yPosition = doc.lastAutoTable.finalY + 8;
          } else {
            doc.setFontSize(9);
            doc.setFont(undefined, 'italic');
            doc.text('No reviews available for this period', 14, yPosition);
            yPosition += 8;
          }
        });
        
        yPosition += 5;
      });
      
      doc.save(`${department?.name}_Comprehensive_Report.pdf`);
      toast.success('Comprehensive report downloaded successfully');
    } catch (error) {
      console.error('Error generating comprehensive report:', error);
      toast.error('Failed to generate comprehensive report');
    }
  };

  useEffect(() => {
    if (deptId) {
      fetchDepartmentData();
    }
  }, [deptId]);

  const fetchDepartmentData = async () => {
    try {
      const [deptRes, krasRes, kpisRes, reviewsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/departments/${deptId}`),
        axios.get(`${API}/kras/${deptId}`),
        axios.get(`${API}/kpis/department/${deptId}`),
        axios.get(`${API}/reviews/department/${deptId}`),
        axios.get(`${API}/analytics/department/${deptId}`)
      ]);
      
      setDepartment(deptRes.data);
      setKras(krasRes.data);
      setKpis(kpisRes.data);
      setReviews(reviewsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching department data:', error);
      toast.error('Failed to load department data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-purple-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/school-role-selection')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow-md flex items-center space-x-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Review Management System</span>
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {department?.name} Department
              </h1>
              <p className="text-slate-600 mt-1">View your KRAs, KPIs, and performance reviews</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white shadow-lg border-l-4 border-l-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total KRAs</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-kras">{kras.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white shadow-lg border-l-4 border-l-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total KPIs</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="total-kpis">{kpis.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white shadow-lg border-l-4 border-l-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Average Score</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="avg-score">
                  {analytics?.average_score ? `${analytics.average_score.toFixed(1)}%` : 'N/A'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Total Weightage Card */}
        {kpis.length > 0 && (
          <Card className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 shadow-lg mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-indigo-900 mb-1">Total Weightage Assigned</p>
                <p className="text-2xl font-bold text-indigo-700">
                  {kpis.reduce((sum, kpi) => sum + kpi.weightage, 0).toFixed(1)}%
                </p>
              </div>
              <div className={`px-4 py-2 rounded-lg font-bold ${
                kpis.reduce((sum, kpi) => sum + kpi.weightage, 0) === 100 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-orange-100 text-orange-700'
              }`}>
                {kpis.reduce((sum, kpi) => sum + kpi.weightage, 0) === 100 ? 'Complete' : 'Incomplete'}
              </div>
            </div>
          </Card>
        )}

        {/* KRAs and KPIs */}
        <Card className="p-6 bg-white shadow-lg mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Your KRAs & KPIs</h2>
            {kras.length > 0 && (
              <div className="flex gap-3">
                <Button
                  onClick={downloadKRAsKPIsPDF}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="download-kras-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download KRAs/KPIs
                </Button>
                {kpis.some(kpi => kpi.sop) && (
                  <Button
                    onClick={downloadSOPsPDF}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="download-sops-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download SOPs
                  </Button>
                )}
              </div>
            )}
          </div>
          {kras.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No KRAs assigned yet. Contact CEO to assign KRAs and KPIs.</p>
          ) : (
            <div className="space-y-6">
              {kras.map((kra) => {
                const kraKpis = kpis.filter((kpi) => kpi.kra_id === kra.id);
                const totalWeightage = kraKpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
                
                return (
                  <div key={kra.id} className="border-2 border-slate-200 rounded-xl p-6 hover:border-purple-300 transition-colors" data-testid="kra-card">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{kra.title}</h3>
                    {kra.description && (
                      <p className="text-slate-600 mb-4">{kra.description}</p>
                    )}
                    
                    {kraKpis.length > 0 ? (
                      <div className="space-y-3 mt-4">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-sm font-semibold text-slate-700">KPIs:</p>
                          <p className="text-sm text-slate-600">Total Weightage: <span className="font-bold">{totalWeightage}%</span></p>
                        </div>
                        {kraKpis.map((kpi) => (
                          <div key={kpi.id} className="p-4 bg-slate-50 rounded-lg" data-testid="kpi-item">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-semibold text-slate-900">{kpi.title}</p>
                                {kpi.description && (
                                  <p className="text-sm text-slate-600 mt-1">{kpi.description}</p>
                                )}
                              </div>
                              <div className="ml-4 text-right">
                                <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-bold">
                                  {kpi.weightage}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* SOPs Section - After all KPIs */}
                        {kraKpis.some(kpi => kpi.sop) && (
                          <div className="mt-4 space-y-3">
                            {kraKpis.filter(kpi => kpi.sop).map((kpi) => (
                              <Collapsible 
                                key={kpi.id}
                                open={openSOP[kpi.id]} 
                                onOpenChange={() => toggleSOP(kpi.id)}
                              >
                                <CollapsibleTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="w-full justify-between hover:bg-purple-50"
                                    data-testid="sop-toggle-button"
                                  >
                                    <span className="font-semibold text-purple-700">
                                      {openSOP[kpi.id] ? 'Hide' : 'View'} SOP for "{kpi.title}"
                                    </span>
                                    {openSOP[kpi.id] ? (
                                      <ChevronUp className="w-4 h-4 text-purple-700" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-purple-700" />
                                    )}
                                  </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-l-purple-500 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="text-white text-xs font-bold">!</span>
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-xs font-bold text-purple-900 mb-2">Step-by-Step Guide:</p>
                                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{kpi.sop}</p>
                                      </div>
                                    </div>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm mt-2">No KPIs defined for this KRA</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Performance Trend */}
        {analytics?.performance_trend?.length > 0 && (
          <Card className="p-6 bg-white shadow-lg mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Performance Overview</h2>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={(() => {
                      const performanceData = analytics.performance_trend.reduce((acc, item) => {
                        const category = getPerformanceCategory(item.score);
                        const existing = acc.find(d => d.name === category);
                        if (existing) {
                          existing.value += 1;
                        } else {
                          acc.push({ name: category, value: 1 });
                        }
                        return acc;
                      }, []);
                      return performanceData;
                    })()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => 
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(() => {
                      const performanceData = analytics.performance_trend.reduce((acc, item) => {
                        const category = getPerformanceCategory(item.score);
                        const existing = acc.find(d => d.name === category);
                        if (existing) {
                          existing.value += 1;
                        } else {
                          acc.push({ name: category, value: 1 });
                        }
                        return acc;
                      }, []);
                      return performanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                      ));
                    })()}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 flex justify-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-sm text-slate-700">Meeting Expectation (≥80%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                <span className="text-sm text-slate-700">Average (50-79%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-sm text-slate-700">Below Expectation (&lt;50%)</span>
              </div>
            </div>
          </Card>
        )}

        {/* Review History */}
        <Card className="p-6 bg-white shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Review History</h2>
            {reviews.length > 0 && (
              <Button
                onClick={downloadReviewsPDF}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="download-reviews-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </div>

          {/* Month-wise Performance Trend Chart */}
          {reviews.length > 0 && (
            <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Month-wise Performance Trend</h3>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={(() => {
                      // Sort reviews by date (year and month)
                      const sortedReviews = [...reviews].sort((a, b) => {
                        const dateA = new Date(`${a.month} 1, ${a.year}`);
                        const dateB = new Date(`${b.month} 1, ${b.year}`);
                        return dateA - dateB;
                      });

                      // Create data points showing overall department score per month
                      return sortedReviews.map(review => ({
                        name: `${review.month.substring(0, 3)} ${review.year}`,
                        fullName: `${review.month} ${review.year}`,
                        score: review.overall_score || 0,
                        totalPossible: 100
                      }));
                    })()}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#64748b"
                      style={{ fontSize: '12px', fontWeight: '500' }}
                    />
                    <YAxis 
                      label={{ value: 'Overall Score (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#64748b' } }} 
                      domain={[0, 100]}
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-4 border-2 border-purple-300 rounded-lg shadow-lg">
                              <p className="font-bold text-slate-900 mb-2">{data.fullName}</p>
                              <div className="space-y-1">
                                <div className="flex justify-between items-center gap-6">
                                  <span className="text-sm text-slate-600">Overall Score:</span>
                                  <span className="text-lg font-bold text-purple-600">
                                    {data.score.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                  <span className="text-xs text-slate-500">Target: {data.totalPossible}%</span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6', r: 5, strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 7, strokeWidth: 2 }}
                    />
                    {/* Target line at 100% */}
                    <Line
                      type="monotone"
                      dataKey="totalPossible"
                      stroke="#22c55e"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                  <span className="text-xs text-slate-700 font-medium">Actual Performance</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-green-500" style={{ borderTop: '2px dashed #22c55e' }}></div>
                  <span className="text-xs text-slate-700 font-medium">Target (100%)</span>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-4 text-center italic">
                This chart shows the overall department performance trend across different review periods
              </p>
            </div>
          )}

          {reviews.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No reviews yet</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => {
                const totalWeightage = kpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
                
                // Group KPIs by KRA
                const kraGroups = kras.map(kra => ({
                  kra,
                  kpis: kpis.filter(kpi => kpi.kra_id === kra.id)
                })).filter(group => group.kpis.length > 0);
                
                return (
                  <div key={review.id} className="border-2 border-slate-200 rounded-xl p-6 hover:border-purple-300 transition-colors" data-testid="review-card">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{review.month} {review.year}</h3>
                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="text-sm text-slate-600">Overall Score:</span>
                          <span className="text-2xl font-bold text-purple-600">{review.overall_score.toFixed(1)}%</span>
                          <span className="text-sm text-slate-500">/ {totalWeightage.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                    
                    {review.overall_feedback && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm font-semibold text-slate-700 mb-1">Promoter's Feedback:</p>
                        <p className="text-slate-600">{review.overall_feedback}</p>
                      </div>
                    )}
                    
                    <div className="mt-4 space-y-4">
                      <p className="text-sm font-semibold text-slate-700">KRA & KPI Performance:</p>
                      {kraGroups.map((group) => (
                        <div key={group.kra.id} className="bg-blue-50 p-4 rounded-lg border-l-4 border-l-blue-500">
                          <h4 className="font-bold text-slate-900 mb-2">{group.kra.title}</h4>
                          {group.kra.description && (
                            <p className="text-sm text-slate-600 mb-3 italic">{group.kra.description}</p>
                          )}
                          <div className="space-y-2">
                            {group.kpis.map((kpi) => {
                              const kpiRev = review.kpi_reviews.find(kr => kr.kpi_id === kpi.id);
                              return (
                                <div key={kpi.id} className="p-3 bg-white border border-slate-200 rounded-lg">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">{kpi.title}</p>
                                      {kpiRev?.feedback && (
                                        <p className="text-xs text-slate-600 mt-1">{kpiRev.feedback}</p>
                                      )}
                                      
                                      {/* Display Attachment if available */}
                                      {kpiRev?.evidence_file && (
                                        <div className="mt-2 flex items-center gap-2">
                                          <FileText className="w-4 h-4 text-blue-600" />
                                          <div className="flex items-center gap-3">
                                            <span className="text-xs text-slate-600">
                                              {kpiRev.evidence_file.original_filename}
                                            </span>
                                            <div className="flex gap-1">
                                              {/* View Attachment Button - Opens in new tab for preview */}
                                              {['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(kpiRev.evidence_file.file_type.toLowerCase()) && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-6 px-2 text-xs"
                                                  onClick={() => window.open(`${API}/view-evidence/${kpiRev.evidence_file.filename}`, '_blank')}
                                                  title="View Attachment"
                                                >
                                                  <Eye className="w-3 h-3 mr-1" />
                                                  View
                                                </Button>
                                              )}
                                              {/* Download Button */}
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-6 px-2 text-xs"
                                                onClick={() => {
                                                  const link = document.createElement('a');
                                                  link.href = `${API}/download-evidence/${kpiRev.evidence_file.filename}`;
                                                  link.download = kpiRev.evidence_file.original_filename;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  document.body.removeChild(link);
                                                }}
                                                title="Download Attachment"
                                              >
                                                <Download className="w-3 h-3 mr-1" />
                                                Download
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <span className="ml-4 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-bold text-sm">
                                      {kpiRev?.achieved_percentage || 0}% / {kpi.weightage}%
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Comprehensive Report Section - Positioned at the End */}
        <Card className="p-6 mb-8 bg-white shadow-lg">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Comprehensive Report</h2>
            <p className="text-sm text-slate-600">Download a detailed report with all KRAs, KPIs, and review history</p>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  Start Month (Optional)
                </label>
                <input
                  type="month"
                  value={reportStartMonth}
                  onChange={(e) => setReportStartMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="YYYY-MM"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  End Month (Optional)
                </label>
                <input
                  type="month"
                  value={reportEndMonth}
                  onChange={(e) => setReportEndMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="YYYY-MM"
                />
              </div>
              
              <div className="flex items-end">
                <Button
                  onClick={() => downloadComprehensiveReport(reportStartMonth, reportEndMonth)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="download-comprehensive-report-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Comprehensive Report
                </Button>
              </div>
            </div>
            
            {reportStartMonth || reportEndMonth ? (
              <p className="text-sm text-slate-500 italic">
                Report will include reviews from {reportStartMonth || 'beginning'} to {reportEndMonth || 'present'}
              </p>
            ) : (
              <p className="text-sm text-slate-500 italic">
                Leave dates empty to include all reviews
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}